import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  CalendarCheck,
  Eye,
  EyeOff,
  Loader2,
  MessageCircle,
  Scissors,
  ShieldCheck
} from 'lucide-react';
import { login } from '../utils/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugLogs, setDebugLogs] = useState([]);

  const [formData, setFormData] = useState({
    username: searchParams.get('slug') || searchParams.get('id') || '',
    senha: ''
  });

  const addDebugLog = (message) => {
    setDebugLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const barbershopId = localStorage.getItem('barbershopId');

    if (token && barbershopId) {
      navigate(`/dashboard/${barbershopId}`);
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    addDebugLog('Iniciando processo de login...');

    try {
      addDebugLog(`Enviando login: usuario=${formData.username}`);
      const response = await login(formData.username, formData.senha);
      addDebugLog('Resposta recebida do servidor');

      const { token, barbershop } = response.data;
      addDebugLog(`Login bem-sucedido para barbearia: ${barbershop.nome}`);

      localStorage.setItem('token', token);
      localStorage.setItem('barbershopId', barbershop.id);
      localStorage.setItem('barbershopName', barbershop.nome);
      localStorage.setItem('barbershopSlug', barbershop.slug || formData.username);

      addDebugLog('Dados salvos no localStorage');
      addDebugLog('Redirecionando para dashboard...');
      navigate(barbershop.onboarding_completed === false ? `/dashboard/${barbershop.id}/onboarding` : `/dashboard/${barbershop.id}`);
    } catch (err) {
      addDebugLog(`ERRO: ${err.message}`);
      addDebugLog(`Status: ${err.response?.status}`);
      addDebugLog(`Response: ${JSON.stringify(err.response?.data || {})}`);

      const errorMsg = err.response?.data?.error || 'Erro ao fazer login';
      setError(errorMsg);

      if (err.response?.data?.expired) {
        addDebugLog('Plano expirado, redirecionando...');
        const expiredBarbershop = err.response?.data?.barbershop;
        if (expiredBarbershop?.id) {
          localStorage.setItem('barbershopId', expiredBarbershop.id);
          localStorage.setItem('barbershopName', expiredBarbershop.nome || '');
          localStorage.setItem('barbershopSlug', expiredBarbershop.slug || formData.username);
        }
        navigate('/expired');
      }
    } finally {
      setLoading(false);
      addDebugLog('Processo finalizado');
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 text-white">
      <div className="min-h-screen grid lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative hidden overflow-hidden lg:flex">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1600&auto=format&fit=crop')] bg-cover bg-center" />
          <div className="absolute inset-0 bg-gradient-to-br from-dark-900/95 via-dark-900/80 to-primary-950/65" />

          <div className="relative z-10 flex w-full flex-col justify-between p-10 xl:p-14">
            <Link to="/" className="inline-flex w-fit items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-600 to-purple-600 shadow-lg shadow-primary-600/25">
                <Scissors className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold">
                Barber<span className="text-primary-300">Flow</span>
              </span>
            </Link>

            <div className="max-w-xl space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary-400/30 bg-primary-500/10 px-3 py-1.5 text-sm text-primary-100">
                <ShieldCheck className="h-4 w-4" />
                Dashboard seguro para sua barbearia
              </div>

              <div>
                <h1 className="text-balance text-5xl font-bold leading-tight xl:text-6xl">
                  Controle agenda, clientes e pagamentos em um painel premium.
                </h1>
                <p className="mt-5 max-w-lg text-lg leading-8 text-gray-300">
                  Acesse sua conta para acompanhar atendimentos, automatizar WhatsApp e manter sua operacao organizada.
                </p>
              </div>

              <div className="grid max-w-lg grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                  <CalendarCheck className="mb-3 h-5 w-5 text-primary-300" />
                  <p className="text-sm text-gray-300">Agenda em tempo real com status dos cortes.</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur">
                  <MessageCircle className="mb-3 h-5 w-5 text-primary-300" />
                  <p className="text-sm text-gray-300">Mensagens automaticas via WhatsApp Worker.</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-400">BarberFlow SaaS para barbearias modernas.</p>
          </div>
        </section>

        <main className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-6 lg:px-10">
          <div className="w-full max-w-md">
            <div className="mb-8 flex items-center justify-between lg:hidden">
              <Link to="/" className="inline-flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-600 to-purple-600">
                  <Scissors className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold">
                  Barber<span className="text-primary-400">Flow</span>
                </span>
              </Link>
            </div>

            <div className="mb-7">
              <p className="text-sm font-medium text-primary-300">Area da barbearia</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight">Entre no seu painel</h2>
              <p className="mt-2 text-sm text-gray-400">
                Use o usuario amigavel da sua barbearia para acessar o dashboard.
              </p>
            </div>

            <div className="card border-primary-500/20 bg-dark-800/90 p-6 shadow-2xl shadow-primary-950/30 sm:p-8">
              {error && (
                <div className="mb-5 flex items-start gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="label">Usuario da barbearia</label>
                  <input
                    type="text"
                    required
                    autoComplete="username"
                    placeholder="ex: imperio-barber"
                    className="input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Esse e o nome de acesso criado para sua barbearia.
                  </p>
                </div>

                <div>
                  <label className="label">Senha</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      placeholder="Digite sua senha"
                      className="input pr-12"
                      value={formData.senha}
                      onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-dark-600 hover:text-gray-200"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary mt-2 w-full">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    <>
                      Entrar no dashboard
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 border-t border-dark-600 pt-6">
                <p className="text-center text-sm text-gray-400">
                  Ainda nao tem uma conta?{' '}
                  <Link to="/" className="font-medium text-primary-300 hover:text-primary-200">
                    Criar barbearia
                  </Link>
                </p>
              </div>
            </div>

            {debugLogs.length > 0 && import.meta.env.DEV && (
              <div className="mt-6 rounded-lg border border-dark-700 bg-dark-800 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-300">Debug Logs</h3>
                  <button
                    onClick={() => setDebugLogs([])}
                    className="text-xs text-gray-500 hover:text-gray-300"
                  >
                    Limpar
                  </button>
                </div>
                <div className="max-h-32 space-y-1 overflow-y-auto">
                  {debugLogs.map((log, index) => (
                    <div key={index} className="font-mono text-xs text-gray-400">
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-8 text-center text-sm text-gray-500">
              © 2026 BarberFlow. Sistema de gestao para barbearias.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
